import React, { useEffect, useRef, useState } from 'react';

import { Box } from './Box';

export interface InfiniteLoadTriggerProps {
  children: React.ReactNode;
  onFetch: () => any;
  fetching: boolean;
  /** Buffer height in pixels to trigger loading before reaching the trigger point */
  bufferPx?: number;
  /** Ref to the scrollable container to use as the observer root */
  root?: React.RefObject<HTMLElement>;
}

export const InfiniteLoadTrigger: React.FC<InfiniteLoadTriggerProps> = ({
  fetching,
  onFetch,
  children,
  bufferPx = 0,
  root,
  ...other
}) => {
  const triggerRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [isOnScreen, setIsOnScreen] = useState(false);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        setIsOnScreen(entry.isIntersecting);
      },
      {
        root: root?.current || null,
        // Add buffer to bottom margin to trigger earlier when scrolling down
        rootMargin: `0px 0px ${bufferPx}px 0px`,
        threshold: 0,
      }
    );
    if (triggerRef.current) {
      observerRef.current.observe(triggerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [bufferPx, root]);

  useEffect(() => {
    if (isOnScreen && !fetching) {
      onFetch();
    }
  }, [isOnScreen, onFetch, fetching]);

  return (
    <Box
      component="span"
      ref={triggerRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        // Make sure the trigger point is visible in the viewport
        minHeight: '10px',
      }}
      {...other}
    >
      {children}
    </Box>
  );
};
